import { convertUTCTimeToLocaleTimezone } from '@utils/time.js';
import { i18n } from '@boot/i18n.js';
import { getAsset } from '@store/facilities/getters.js';

export default class DataEntry {
    constructor(
        chartData,
        reportStart,
        reportEnd,
        resolution = 'hourly',
        timezone,
        filters,
        locale = 'en-us'
    ) {
        this.data = chartData;
        this.selectedResolution = resolution;
        this.col = 0;
        this.timezone = timezone;
        this.userLocale = locale;
        this.filters = filters;

        this.startDate = this.getDate(reportStart);
        this.endDate = this.getDate(reportEnd);
        this.step = this.getStep();

        this.dateColumnMap = {};
        this.groupedAssetData = {};
        this.chartMatrix = [];
        this.yLabels = {};
        this.assetMap = new Map();

        this.totalMajorEvents = 0;
        this.totalJustifiedEvents = 0;
    }

    // arbitrary negative number since null is ignored by e-charts
    // so we need some integer value to determine the color of the cell.
    static defaultPercentageValue = -100000;
    static defaultCellFontColor = 'gray';

    /**
     * @param {String} date
     * @returns {Object} date object converted to selected timezone.
     */
    getDate(date) {
        return new Date(
            convertUTCTimeToLocaleTimezone(date, this.timezone, this.userLocale)
        );
    }

    /**
     * @param {Object} date
     * @returns {Object} date object with incremented hour.
     */
    hourlyResolution(date) {
        let nextHour = new Date(date.setHours(date.getHours() + 1));
        nextHour.setMinutes(0);
        nextHour.setSeconds(0);
        return nextHour;
    }

    /**
     * @param {Object} date
     * @returns {Object} date object with incremented day.
     */
    dailyResolution(date) {
        let nextDate = new Date(date.setDate(date.getDate() + 1));
        nextDate.setHours(0);
        nextDate.setMinutes(0);
        nextDate.setSeconds(0);
        return nextDate;
    }

    /**
     * @param {Object} date
     * @returns {Object} date object with incremented week
     */
    weeklyResolution(date) {
        let nextWeek = new Date(date.setDate(date.getDate() + 7));
        const selectedDate = nextWeek.getDate();
        const dayOfTheWeek = nextWeek.getDay();
        const weekDifference =
            selectedDate - dayOfTheWeek + (dayOfTheWeek === 0 ? -6 : 1);
        const weeklyDate = new Date(nextWeek.setDate(weekDifference));
        weeklyDate.setHours(0);
        weeklyDate.setMinutes(0);
        weeklyDate.setSeconds(0);
        return weeklyDate;
    }

    /**
     * @param {Object} date
     * @returns {Object} date object with incremented month
     */
    monthlyResolution(date) {
        let nextMonth = new Date(date.setMonth(date.getMonth() + 1));
        nextMonth.setHours(0);
        nextMonth.setMinutes(0);
        nextMonth.setSeconds(0);
        return nextMonth;
    }

    /**
     * @returns pointer to function according to the
     * selected resolution.
     */
    getStep() {
        const resolutionMethodMap = {
            hourly: this.hourlyResolution,
            daily: this.dailyResolution,
            weekly: this.weeklyResolution,
            monthly: this.monthlyResolution
        };
        return resolutionMethodMap[this.selectedResolution];
    }

    /**
     * Creates a date column map
     */
    populateDateColumnMap() {
        while (this.startDate <= this.endDate) {
            this.dateColumnMap[this.startDate.toISOString()] = this.col;
            this.col += 1;
            this.startDate = this.step(this.startDate);
        }
    }

    calculatePerformancePercentage(majorEvents, justifiedEvents) {
        if (majorEvents === 0) {
            return 100;
        }
        return Math.round((justifiedEvents / majorEvents) * 100);
    }

    /**
     * Iterates over the sortedApiData and group it per Asset
     */
    groupPerAsset() {
        let rowIndex = 0;
        for (let item of this.getSortedApiData()) {
            if (this.filters.assetIds.has(item.asset_id)) {
                if (!this.groupedAssetData[item.asset_id]) {
                    const asset = getAsset(item.asset_id);
                    const isDeleted = asset ? asset.is_deleted || false : false;
                    this.groupedAssetData[item.asset_id] = {
                        label: !isDeleted
                            ? item.asset_name
                            : i18n.global.t('{assetName} (Deleted)', {
                                  assetName: item.asset_name
                              }),
                        rowIndex: rowIndex
                    };
                    this.assetMap.set(item.asset_name, item.asset_id);
                    rowIndex += 1;
                }
                this.groupedAssetData[item.asset_id][
                    new Date(
                        convertUTCTimeToLocaleTimezone(
                            item.period,
                            this.timezone,
                            this.userLocale
                        )
                    ).toISOString()
                ] = [
                    this.dateColumnMap[
                        new Date(
                            convertUTCTimeToLocaleTimezone(
                                item.period,
                                this.timezone,
                                this.userLocale
                            )
                        ).toISOString()
                    ],
                    this.groupedAssetData[item.asset_id].rowIndex,
                    this.calculatePerformancePercentage(
                        item.major_events,
                        item.justified_events
                    )
                ];
                // accumulate the major events and justified events
                this.totalMajorEvents += item.major_events;
                this.totalJustifiedEvents += item.justified_events;
            }
        }
    }

    /**
     * Iterate over the grouped asset data and generate the
     * final chart matrix as well as populates the y axis labels.
     */
    buildData() {
        for (let item in this.groupedAssetData) {
            this.yLabels[this.groupedAssetData[item].rowIndex] =
                this.groupedAssetData[item].label;
            for (let date in this.dateColumnMap) {
                const dataEntryPerformancePoint =
                    this.groupedAssetData[item][date]; //array
                if (dataEntryPerformancePoint) {
                    const dataEntryPoint = {
                        value: dataEntryPerformancePoint
                    };
                    // if the data entry performance point falls between
                    // 60 to 80 percent change the cell font color to gray
                    // for better visibility, skip otherwise.
                    if (
                        dataEntryPerformancePoint[2] > 60 &&
                        dataEntryPerformancePoint[2] <= 80
                    ) {
                        dataEntryPoint['label'] = {
                            color: DataEntry.defaultCellFontColor
                        };
                    }
                    this.chartMatrix.push(dataEntryPoint);
                } else {
                    this.chartMatrix.push({
                        value: [
                            this.dateColumnMap[date],
                            this.groupedAssetData[item].rowIndex,
                            DataEntry.defaultPercentageValue
                        ],
                        // this will enable these value to skip the color calculation being done by visual map
                        visualMap: false,
                        // this will set the font color of
                        label: {
                            color: DataEntry.defaultCellFontColor
                        }
                    });
                }
            }
        }
    }

    createData() {
        this.populateDateColumnMap();
        this.groupPerAsset();
        this.buildData();
    }

    /**
     * generates labels which will be placed on x Axis
     * of the heat map with respect to the selected
     * resolution.
     * @returns X Axis Labels to be placed on heat map
     */
    getXAxisLabels() {
        const dateColumnObjects = Object.keys(this.dateColumnMap);
        if (this.selectedResolution === 'monthly') {
            let months = [
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December'
            ];

            // if the selected date range is greater than 12 months
            // then show the year as well.
            if (dateColumnObjects.length > 12) {
                return new Set(
                    dateColumnObjects.map(date => {
                        return `${
                            months[new Date(date).getMonth()]
                        }\n${new Date(date).getFullYear()}`;
                    })
                );
            } else {
                return new Set(
                    dateColumnObjects.map(date => {
                        return months[new Date(date).getMonth()];
                    })
                );
            }
        }
        if (
            this.selectedResolution === 'daily' ||
            this.selectedResolution === 'weekly'
        ) {
            return new Set(
                dateColumnObjects.map(date =>
                    new Date(date).toLocaleDateString()
                )
            );
        }
        return new Set(
            dateColumnObjects.map(date => {
                return new Date(date).toLocaleString();
            })
        );
    }

    /**
     * Sort the received api data with the asset name.
     * @returns Api data sorted with asset name alphabetically
     * descending order (Z-A)
     */
    getSortedApiData() {
        return this.data.sort((a, b) => {
            const firstAssetName = a.asset_name.toLowerCase();
            const secondAssetName = b.asset_name.toLowerCase();
            if (firstAssetName < secondAssetName) {
                return 1;
            }
            if (firstAssetName > secondAssetName) return -1;
            return 0;
        });
    }

    /**
     * generates labels which will be placed on y Axis
     * of the heat map.
     * @returns Y Axis Labels to be placed on heat map
     */
    getYAxisLabels() {
        let expectedIndex = 0;
        return Object.keys(this.yLabels).map(index => {
            if (parseInt(index) === expectedIndex) {
                expectedIndex += 1;
                return this.yLabels[index];
            }
        });
    }

    /**
     * @returns final structured data specifically prepared
     * for heat map.
     */
    getData() {
        return this.chartMatrix;
    }

    /**
     * @returns the starting dates of each heatmap cell
     */
    getDateMap() {
        return Object.keys(this.dateColumnMap);
    }

    /**
     * @returns the asset map which contains the asset id and asset name
     */
    getAssetMap() {
        return this.assetMap;
    }

    /**
     * @returns total of major events for the data entry report
     */
    getTotalMajorEvents() {
        return this.totalMajorEvents;
    }

    /**
     * @returns total of justified events for the data entry report
     */
    getTotalJustifiedEvents() {
        return this.totalJustifiedEvents;
    }

    /**
     * @returns total of justified events for the data entry report
     */
    getTotalUnjustifiedEvents() {
        return this.totalMajorEvents - this.totalJustifiedEvents;
    }

    /**
     * @returns percentage score for the data entry report
     */
    getDataEntryScore() {
        if (this.totalMajorEvents === 0) {
            return 100;
        }
        const percentage =
            (this.totalJustifiedEvents / this.totalMajorEvents) * 100;
        return percentage;
    }
}
